/**
 * Accessible text field component with formik integration
 *
 * @author Claude
 */
import { TextField, TextFieldProps } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { memo } from 'react';

interface FormTextFieldProps extends Omit<TextFieldProps, 'name'> {
  name: string;
  label: string;
  helperText?: string;
}

/**
 * Text field component with Formik integration and accessibility features
 * @param {FormTextFieldProps} props - Component props
 * @returns {JSX.Element} Accessible form text field
 *
 * @a11y Uses aria-invalid for validation state
 * @a11y Uses aria-describedby to connect error messages
 * @a11y Handles required state properly
 */
export const FormTextField = memo(
  ({ name, label, helperText, required, ...rest }: FormTextFieldProps) => {
    const [field, meta] = useField(name);
    const { submitCount } = useFormikContext();
    // Only show errors after the user has submitted the form or modified the field value.
    // This prevents showing "required" errors before the user has interacted with the form.
    const fieldDirty = meta.value !== meta.initialValue;
    const shouldShowError = meta.error && meta.touched && (submitCount > 0 || fieldDirty);
    const errorText = shouldShowError ? meta.error : '';
    const hasError = Boolean(errorText);

    // Generate a unique ID for accessibility
    const id = `field-${name}`;
    const errorId = hasError ? `${id}-error` : undefined;
    const helperId = helperText && !hasError ? `${id}-helper` : undefined;

    return (
      <TextField
        {...field}
        {...rest}
        id={id}
        label={label}
        variant="outlined"
        fullWidth
        required={required}
        error={hasError}
        helperText={errorText || helperText}
        InputLabelProps={{ shrink: true }}
        aria-describedby={errorId ? errorId : helperId ? helperId : undefined}
        aria-invalid={hasError}
        aria-required={required}
        FormHelperTextProps={{
          id: hasError ? errorId : helperId,
        }}
      />
    );
  }
);

export default FormTextField;
